import {
  AppointmentDepositPlacement,
  AppointmentsSchedulingType,
} from '@prisma/client';
import {
  Employee,
  Input,
  InputGroupType,
  InputRowType,
  Option,
} from '../../interfaces';
import { getArtistName } from '../global/helper';
import { AcceptFormState } from '../../types/appointments';
import { getDepositPlacementSelectInput } from '../../helpers/appointments';

export const initFormValues: AcceptFormState = {
  artist: '',
  appointmentLengthInMinutes: '' as unknown as number,
  numberOfSessions: 1,
  schedulingType: '' as AppointmentsSchedulingType,
  depositPlacement: '' as AppointmentDepositPlacement,
  bufferDays: '' as unknown as number,
  deposit: '' as unknown as boolean,
  depositAmount: 0,
  priceType: '' as unknown as string,
  minPrice: 0,
  maxPrice: 0,
  setPrice: 0,
  hourlyPrice: 0,
  customDesignMessage: '',
};

export const depositOptions: Option[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const priceOptions: Option[] = [
  { value: 'FIXED', label: 'Set Price' },
  { value: 'RANGE', label: 'Price Range' },
  { value: 'HOURLY', label: 'Hourly Price' },
];

export async function getInputGroups(
  artistOptions: Option[],
  lengthOptions: Option[],
  employee: Employee,
  values: AcceptFormState,
  options?: {
    isLoading?: boolean;
    validateApptLength?: (value: string) => boolean | Promise<boolean>;
  },
) {
  const employeeName = getArtistName(employee);

  const appointmentDetails: InputGroupType = {
    name: 'Appointment Details',
    inputRows: [
      [
        {
          name: 'artist',
          label: 'Artist',
          type: 'select',
          options:
            artistOptions.length > 0
              ? artistOptions
              : [{ value: employee.employeeId, label: employeeName }],
          placeholder: 'Select an artist for this appointment',
        },
      ],
      [
        {
          name: 'appointmentLengthInMinutes',
          label: 'Apt. Length',
          type: 'select',
          options: lengthOptions,
          placeholder: 'How long?',
          validateChange: options?.validateApptLength,
          disabled: !values?.artist || options?.isLoading,
        },
      ],
      [
        {
          name: 'deposit',
          label: 'Deposit?',
          type: 'select',
          options: depositOptions,
          placeholder: 'Collect a deposit',
        },
        {
          name: 'depositAmount',
          label: 'Deposit Amount',
          type: 'dollar',
          placeholder: 'Enter amount',
          disabled: {
            start: true,
            field: 'deposit',
            changesWhen: 'true',
          },
        },
      ],
      [
        {
          name: 'priceType',
          label: 'Price Type',
          type: 'select',
          options: priceOptions,
          placeholder: 'Exact, Range, or Hourly',
        },
        {
          name: 'setPrice',
          label: 'Set Price',
          type: 'dollar',
          placeholder: 'Enter price',
          hidden: {
            start: false,
            field: 'priceType',
            changesWhen: 'FIXED',
          },
        },
        {
          name: 'hourlyPrice',
          label: 'Hourly Price',
          type: 'dollar',
          placeholder: 'Enter price',
          hidden: {
            start: true,
            field: 'priceType',
            changesWhen: 'HOURLY',
          },
        },
      ],
      [
        {
          name: 'minPrice',
          label: 'Min Price',
          type: 'dollar',
          placeholder: 'Enter min',
          hidden: {
            start: true,
            field: 'priceType',
            changesWhen: 'RANGE',
          },
        },
        {
          name: 'maxPrice',
          label: 'Max Price',
          type: 'dollar',
          placeholder: 'Enter max',
          hidden: {
            start: true,
            field: 'priceType',
            changesWhen: 'RANGE',
          },
        },
      ],
      [
        {
          name: 'customDesignMessage',
          label: 'Reply Message',
          type: 'textarea',
        },
      ],
    ],
  };

  const sessionOptions: Option[] = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  const schedulingTypeOptions: Option<AppointmentsSchedulingType>[] = [
    {
      value: 'FIRST_SESSION',
      label: 'Client should just schedule the first session',
    },
    { value: 'ALL', label: 'Client should schedule all the sessions' },
    {
      value: 'CLIENT_CHOICE',
      label: 'Let the client choose how many to schedule',
    },
  ];

  const otherDetails: InputGroupType = {
    name: 'Other Details (Optional)',
    inputRows: [
      [
        {
          label: 'Number of Sessions',
          type: 'select',
          placeholder: 'Enter number of sessions',
          options: sessionOptions,
          name: 'numberOfSessions',
        },
      ],
      (values.numberOfSessions ?? 0) > 1 && [
        {
          label: 'Scheduling Type',
          type: 'select',
          options: schedulingTypeOptions,
          placeholder:
            'How do you want clients to schedule these appointments?',
          name: 'schedulingType',
        },
      ],
      (values.numberOfSessions ?? 0) > 1 && [
        getDepositPlacementSelectInput({
          disabled: values.schedulingType === 'FIRST_SESSION',
        }),
      ],
      (values.numberOfSessions ?? 0) > 1 && [
        {
          label: 'Buffer Days',
          type: 'number',
          min: 0,
          step: 1,
          placeholder:
            'How many days should be in-between the customers appointments?',
          name: 'bufferDays',
        } as Input,
      ],
    ].filter(Boolean) as InputRowType[],
  };

  return [appointmentDetails, otherDetails];
}
